import { useState, useEffect } from "react";
import { Building2, Users, Sparkles, CreditCard, Plug, User, Save, Loader2, Bell, Globe, UserCheck, GraduationCap, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TeamManagement } from "@/components/settings/TeamManagement";
import { NotificationPreferences } from "@/components/settings/NotificationPreferences";
import { AvatarUpload } from "@/components/settings/AvatarUpload";
import { CustomerOnboardingAutomation } from "@/components/onboarding/CustomerOnboardingAutomation";
import { ProductTrainingModule } from "@/components/training/ProductTrainingModule";
import { useWizardProgress } from "@/hooks/useWizardProgress";

const TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "Europe/Moscow", label: "Moscow (MSK)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Shanghai", label: "China (CST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
  { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)" },
];

export default function Settings() {
  const { user, profile } = useAuth();
  const { resetWizard } = useWizardProgress();
  const [isLoading, setIsLoading] = useState(false);
  
  // Profile form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Load profile data
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
      setJobTitle(profile.job_title || "");
      setCompanyName(profile.company_name || "");
      setPhone((profile as any).phone || "");
      setTimezone((profile as any).timezone || "UTC");
      setEmail(profile.email || "");
      setAvatarUrl(profile.avatar_url || null);
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          first_name: firstName,
          last_name: lastName,
          job_title: jobTitle,
          company_name: companyName,
          phone: phone,
          timezone: timezone,
        } as any)
        .eq("id", user.id);

      if (error) throw error;
      toast.success("Profile updated successfully");
    } catch (error: any) {
      toast.error(`Failed to update profile: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account and preferences
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="organization" className="gap-2">
            <Building2 className="h-4 w-4" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" />
            Team
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-2">
            <Sparkles className="h-4 w-4" />
            AI Config
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Plug className="h-4 w-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="onboarding" className="gap-2">
            <UserCheck className="h-4 w-4" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="training" className="gap-2">
            <GraduationCap className="h-4 w-4" />
            Training
          </TabsTrigger>
          <TabsTrigger value="billing" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Billing
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">Profile Photo</h3>
            {user && (
              <AvatarUpload
                userId={user.id}
                currentAvatarUrl={avatarUrl}
                firstName={firstName}
                lastName={lastName}
                onAvatarChange={setAvatarUrl}
              />
            )}
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">Your Profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobTitle">Job Title</Label>
                <Input
                  id="jobTitle"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="Enter your job title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter your company name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger id="timezone" className="w-full">
                    <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border border-border z-50">
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  disabled
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed here. Contact support if you need to update it.
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-border flex justify-end">
              <Button onClick={handleSaveProfile} disabled={isLoading} className="gap-2">
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Profile
              </Button>
            </div>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">Security</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Change Password</p>
                  <p className="text-sm text-muted-foreground">
                    Update your account password
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Update Password
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-muted-foreground">
                    Add an extra layer of security
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Enable 2FA
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Organization Tab */}
        <TabsContent value="organization" className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">Organization Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input defaultValue="Acme Corp" />
              </div>
              <div className="space-y-2">
                <Label>Subdomain</Label>
                <div className="flex items-center gap-2">
                  <Input defaultValue="acme" className="flex-1" />
                  <span className="text-muted-foreground">.aiveliq360.com</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Industry</Label>
                <Input defaultValue="Technology" />
              </div>
              <div className="space-y-2">
                <Label>Default Currency</Label>
                <Input defaultValue="USD" />
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-border flex justify-end">
              <Button onClick={() => toast.info("Organization settings saved", { description: "Your organization details have been updated" })}>
                Save Changes
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <TeamManagement />
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <NotificationPreferences />
        </TabsContent>

        {/* AI Config Tab */}
        <TabsContent value="ai" className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">AI Model Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>AI Provider</Label>
                <Input defaultValue="Lovable AI" disabled className="bg-muted/50" />
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Input defaultValue="google/gemini-2.5-flash" disabled className="bg-muted/50" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              AI configuration is managed by Lovable Cloud. The AI gateway automatically selects the optimal model for each task.
            </p>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">AI Features</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Lead Scoring</p>
                  <p className="text-sm text-muted-foreground">
                    AI-powered lead scoring and prioritization
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Deal Scoring</p>
                  <p className="text-sm text-muted-foreground">
                    AI-powered deal win probability prediction
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Call Analysis</p>
                  <p className="text-sm text-muted-foreground">
                    Automatic call transcription and sentiment analysis
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Proposal Generation</p>
                  <p className="text-sm text-muted-foreground">
                    Auto-generate proposals with AI
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Outreach Personalization</p>
                  <p className="text-sm text-muted-foreground">
                    AI-personalized email and message content
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
            </div>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">Sales Wizard</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Reset Wizard Progress</p>
                <p className="text-sm text-muted-foreground">
                  Clear all wizard progress and start fresh from step 1
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  resetWizard();
                  toast.success("Wizard progress reset", { description: "Starting fresh from step 1" });
                }}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset Wizard
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: "Email (SMTP)", status: "connected", icon: "📧" },
              { name: "Twilio (SMS/Voice)", status: "connected", icon: "📱" },
              { name: "WhatsApp Business", status: "pending", icon: "💬" },
              { name: "Stripe Billing", status: "not_connected", icon: "💳" },
              { name: "DocuSign", status: "connected", icon: "✍️" },
              { name: "Slack", status: "not_connected", icon: "💬" },
            ].map((integration) => (
              <div key={integration.name} className="glass rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{integration.icon}</span>
                    <div>
                      <p className="font-medium">{integration.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {integration.status.replace("_", " ")}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={
                      integration.status === "connected" ? "outline" : "default"
                    }
                    size="sm"
                    onClick={() => toast.info(`${integration.name} integration`, { 
                      description: integration.status === "connected" 
                        ? "Configure your integration settings" 
                        : "Contact support to enable this integration" 
                    })}
                  >
                    {integration.status === "connected"
                      ? "Configure"
                      : "Connect"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold">Current Plan</h3>
                <p className="text-muted-foreground">Enterprise</p>
              </div>
              <span className="stage-pill bg-success/10 text-success">
                Active
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Monthly Cost</p>
                <p className="text-2xl font-bold">$299</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Users</p>
                <p className="text-2xl font-bold">12 / 25</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">AI Credits</p>
                <p className="text-2xl font-bold">8,450</p>
              </div>
            </div>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">Billing History</h3>
            <div className="space-y-3">
              {[
                { date: "Dec 1, 2025", amount: "$299.00", status: "Paid" },
                { date: "Nov 1, 2025", amount: "$299.00", status: "Paid" },
                { date: "Oct 1, 2025", amount: "$299.00", status: "Paid" },
              ].map((invoice, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div>
                    <p className="font-medium text-sm">{invoice.date}</p>
                    <p className="text-xs text-muted-foreground">Monthly subscription</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium">{invoice.amount}</span>
                    <span className="stage-pill bg-success/10 text-success text-xs">
                      {invoice.status}
                    </span>
                    <Button variant="ghost" size="sm">
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Customer Onboarding Tab */}
        <TabsContent value="onboarding">
          <CustomerOnboardingAutomation />
        </TabsContent>

        {/* Product Training Tab */}
        <TabsContent value="training">
          <ProductTrainingModule />
        </TabsContent>
      </Tabs>
    </div>
  );
}